import React, { useEffect, useState } from 'react';
import { Grid, TextField, Typography, Card, CardContent, Button, Box } from '@mui/material';

import axios from 'axios';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import LoadingAnimation from '../../../components/LoadingAnimation';


interface SocialHistory {
  smoking: boolean;
  alcohol: boolean;
  drugs: boolean;
  additional_info: { [key: string]: string };
  notes: string;
}

interface PatientHistory {
  id: number;
  patient_id: number;
  consult_id: number | null;
  slug: string;
  values: {
    date: string;
    drug: boolean;
    notes: string[];
    alcohol: boolean;
    tobacco: boolean;
    additional_info: {
      drugs: string;
      alcohol: string;
      smoking: string;
    };
  };
  freeze: number;
}

interface ApiResponse {
  code: number;
  message: string;
  data: {
    patient_histories: PatientHistory[];
  };
}

interface SocialHistoryFormProps {
  patientId: string;
}

const SocialHistoryForm: React.FC<SocialHistoryFormProps> = ({ patientId }) => {
 
  const [formData, setFormData] = useState<SocialHistory>({
    smoking: false,
    alcohol: false,
    drugs: false,
    additional_info: { smoking: '', alcohol: '', drugs: '' },
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingHistoryId, setExistingHistoryId] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const questions: { text: string; key: keyof SocialHistory }[] = [
    { text: 'Smoking', key: 'smoking' },
    { text: 'Alcohol', key: 'alcohol' },
    { text: 'Drugs', key: 'drugs' },
  ];

  const fetchSocialHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get<ApiResponse>(
        `${BASE_URL}/resource/patientHistories?limit=10&page=1&from=&to=&order_by=id&dir=2&user_id=${patientId}&slug=social-history&searchkey=undefined`,
        {
          headers: getAxiosConfig().headers,
        }
      );

      const patientHistories = response.data.data.patient_histories;
      if (patientHistories.length > 0) {
        const history = patientHistories[0];
        setFormData({
          smoking: history.values.tobacco,
          alcohol: history.values.alcohol,
          drugs: history.values.drug,
          additional_info: history.values.additional_info,
          notes: history.values.notes.join(', '),
        });
        setExistingHistoryId(history.id);
        setIsEditMode(false); // Start in view mode after fetching data
      }
    } catch (error) {
      showSnackbar(`${error}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocialHistory();
  }, [patientId]);

  const handleNoteChange = (key: keyof SocialHistory) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      additional_info: { ...prev.additional_info, [key]: event.target.value },
    }));
  };

  const handleToggleSwitch = (key: keyof SocialHistory) => () => {
    setFormData((prevState) => ({ ...prevState, [key]: !prevState[key] }));
  };

  const handleSave = async () => {
    setLoading(true);
    const payload = {
      values: {
        tobacco: formData.smoking,
        alcohol: formData.alcohol,
        drug: formData.drugs,
        additional_info: formData.additional_info,
        notes: [formData.notes],
        date: new Date().toISOString().split('T')[0],
      },
      patient_id: patientId,
      slug: 'social-history',
    };

    try {
      if (existingHistoryId) {
        await axios.patch<ApiResponse>(
          `${BASE_URL}/resource/patientHistories/${existingHistoryId}`,
          payload,
          {
            headers: getAxiosConfig().headers,
          }
        );
      } else {
        await axios.post<ApiResponse>(
          `${BASE_URL}/resource/patientHistories`,
          payload,
          {
            headers: getAxiosConfig().headers,
          }
        );
      }

      fetchSocialHistory();
      showSnackbar('Updated Successfully', 'success');
      setIsEditMode(false); // Exit edit mode after saving
    } catch (error) {
      showSnackbar(`${error}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="items-center justify-center p-4">
     {loading && (
        <div className="inset-0 flex items-center justify-center bg-white bg-opacity-50 z-10">
          <LoadingAnimation />
        </div>
      )}
      <Grid container spacing={2}>
        {questions.map((question, index) => (
          <Grid item xs={12} sm={6} md={4} key={index} className="p-2">
            <Card
              variant="outlined"
              sx={{
                borderRadius: '16px',
                boxShadow: 3,
                fontFamily: 'Jost, sans-serif',
                background: '#f9f9f9',
                transition: '0.3s',
                borderTop: '6px solid #0870b4', // Adjust color and width as needed
                '&:hover': {
                  boxShadow: 6,
                },
              }}

            >
              <CardContent>
                <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', color: '#333', fontFamily: 'Jost, sans-serif' }}>
                  {question.text}
                </Typography>
                {isEditMode ? (
                  <>
                    <div className="flex justify-center items-center">
                      <label className="relative inline-flex items-center cursor-pointer mt-4">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={!!formData[question.key]}
                          onChange={handleToggleSwitch(question.key)}
                          aria-label={`Toggle ${question.text}`} // Accessibility
                        />
                        <div className="w-20 h-10 flex items-center bg-gray-300 rounded-full p-1 transition duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <div
                            className={`w-8 h-8 bg-white rounded-full shadow-md transform transition-transform duration-300 ease-in-out ${formData[question.key] ? 'translate-x-10' : 'translate-x-1'}`}
                          ></div>
                          <span
                            className={`absolute transition-opacity duration-300 ${formData[question.key] ? 'opacity-100 left-2' : 'opacity-0'
                              }`}
                          >
                            Yes
                          </span>
                          <span
                            className={`absolute transition-opacity duration-300 ${formData[question.key] ? 'opacity-0' : 'opacity-100 right-2'
                              }`}
                          >
                            No
                          </span>
                        </div>
                      </label>
                    </div>



                    {formData[question.key] && (
                      <TextField
                        name={`additional_info-${question.key}`}
                        value={formData.additional_info[question.key] || ''}
                        onChange={handleNoteChange(question.key)}
                        fullWidth
                        size="small"
                        margin="dense"
                        // sx={textFieldStyles}
                        placeholder={`Additional Info for ${question.text}`}
                      />
                    )}
                  </>
                ) : (
                  <div>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                        borderRadius: 2,
                        backgroundColor: formData[question.key] ? '#f8d7da' : '#d4edda', // light green or red background
                        border: formData[question.key] ? '1px solid #c3e6cb' : '1px solid #f5c6cb', // border color
                        transition: 'all 0.3s ease', // smooth transition for hover effects
                        '&:hover': {
                          transform: 'scale(1.05)', // scale effect on hover
                        },
                      }}
                    >
                      {formData[question.key] ? (
                        <>
                          <CancelIcon sx={{ color: '#dc3545', marginRight: 1 }} />

                          <Typography variant="body1" align="center" sx={{ color: '#555', fontWeight: 'bold' }}>
                            Yes
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon sx={{ color: '#28a745', marginRight: 1 }} />
                          <Typography variant="body1" align="center" sx={{ color: '#555', fontWeight: 'bold' }}>
                            No
                          </Typography>
                        </>
                      )}
                    </Box>
                    {formData.additional_info[question.key] && (
                      <Box
                        sx={{
                          background: '#d3d3d3', // Background color for better visibility
                          borderRadius: '8px', // Rounded corners
                          padding: '8px', // Padding for spacing
                          boxShadow: 1, // Subtle shadow for depth
                          marginTop: '8px', // Space above the additional info
                        }}
                      >
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: '600',
                            lineHeight: 1.5,
                            marginTop: '8px', // Space above the no data message
                          }}
                        >
                          {formData.additional_info[question.key] || null}
                        </Typography>
                      </Box>

                    )}

                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

         </div>
  );
};

export default SocialHistoryForm;
